import React from 'react';
import { useBalancedEquationsStore } from '../store/useBalancedEquationsStore';
import { CheckCircle2, XCircle } from 'lucide-react';

export function BalancedEquationsTable() {
  const results = useBalancedEquationsStore(s => s.results);
  const sorted = [...results].sort((a, b) => a.part.localeCompare(b.part));

  const equationsCorrect = sorted.filter(r => r.equationCorrect).length;
  const observationsCorrect = sorted.filter(r => r.observationCorrect).length;

  return (
    <div className="flex flex-col h-full">
      <h2 className="text-lg font-bold mb-3">Recorded Findings</h2>
      {sorted.length === 0 && (
        <p className="text-sm text-muted-foreground">
          Complete a part and press <em>Submit Answer</em> to populate this list.
        </p>
      )}

      <div className="flex flex-col gap-3">
        {sorted.map(r => (
          <div key={r.part} className="p-4 rounded-xl bg-card border border-border">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-bold text-sm">
                Part {r.part} — {r.reactantsLabel}
              </h3>
              <div className="flex items-center gap-1">
                {r.observationCorrect && r.equationCorrect ? (
                  <span className="text-emerald-500 text-xs font-semibold flex items-center gap-1">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Recorded
                  </span>
                ) : (
                  <span className="text-rose-500 text-xs font-semibold flex items-center gap-1">
                    <XCircle className="w-3.5 h-3.5" /> Partial
                  </span>
                )}
              </div>
            </div>

            <div className="space-y-2 text-sm">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">Your observation</p>
                <p className="text-foreground italic">"{r.studentObservation}"</p>
                <p className={`text-xs mt-0.5 ${r.observationCorrect ? 'text-emerald-500' : 'text-rose-500'}`}>
                  {r.observationCorrect ? '✓ Captures the key changes' : `✗ Canonical: ${r.canonicalObservation}`}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">Your equation</p>
                <p className="font-mono text-foreground">{r.studentEquation}</p>
                <p className={`text-xs mt-0.5 ${r.equationCorrect ? 'text-emerald-500' : 'text-rose-500'}`}>
                  {r.equationCorrect ? `✓ Correct! Canonical: ${r.canonicalEquation}` : `✗ Canonical: ${r.canonicalEquation}`}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {sorted.length > 0 && (
        <div className="mt-4 grid grid-cols-2 gap-3">
          <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
            <p className="text-[10px] uppercase tracking-wider text-emerald-600 dark:text-emerald-400 font-bold mb-1">
              Parts completed
            </p>
            <p className="font-mono text-lg font-bold">{sorted.length} / 3</p>
          </div>
          <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
            <p className="text-[10px] uppercase tracking-wider text-amber-600 dark:text-amber-400 font-bold mb-1">
              Equations correct
            </p>
            <p className="font-mono text-lg font-bold">{equationsCorrect} / {sorted.length}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default BalancedEquationsTable;
